import { useEffect, useRef, useState } from 'react';
import { benchApi } from '../services/api/benchApi';
import { BENCH_REPORT_FACETS, FACET_COUNT_MODES } from '../constants/benchReportFacets';
import { shouldClearSelectedBusinessUnit } from '../utils/benchBusinessUnitFacets';

/**
 * ADR-108 — Business Unit options for the bench reports: the BUs (with ancestors) that
 * have individuals on bench in the selected Cluster, with counts.
 *
 * Fetches on mount and when the Cluster changes — nothing else (the options do
 * not depend on the other report filters). The current BU selection is sent along so a
 * restored selection is resolved; an out-of-scope selection triggers
 * `onSelectionOutOfScope` (the page clears it), an in-scope one with zero rows is kept.
 *
 * Latest-request-wins: a slow response for a previous Cluster is dropped.
 * `failed` tells the page to degrade to the selector's own organization-hierarchy fetch.
 *
 * @param {Object} args
 * @param {string} args.report - BENCH_REPORT_KEYS value
 * @param {string} args.clusterId - selected Cluster ('' for all)
 * @param {string} args.businessUnitId - current BU selection ('' for none)
 * @param {Function} [args.onSelectionOutOfScope]
 * @returns {{ businessUnitsData: Object[], countMode: string, isLoading: boolean, failed: boolean }}
 */
export const useBenchBusinessUnitFacets = ({ report, clusterId, businessUnitId, onSelectionOutOfScope }) => {
  const [businessUnitsData, setBusinessUnitsData] = useState([]);
  const [countMode, setCountMode] = useState(FACET_COUNT_MODES.SUBTREE);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const businessUnitIdRef = useRef(businessUnitId);
  businessUnitIdRef.current = businessUnitId;
  const onSelectionOutOfScopeRef = useRef(onSelectionOutOfScope);
  onSelectionOutOfScopeRef.current = onSelectionOutOfScope;
  const requestSeqRef = useRef(0);

  useEffect(() => {
    const ticket = requestSeqRef.current + 1;
    requestSeqRef.current = ticket;
    const isCurrent = () => requestSeqRef.current === ticket;

    setIsLoading(true);

    benchApi.getBenchReportFacets({
      report,
      facets: [BENCH_REPORT_FACETS.BUSINESS_UNITS],
      clusterId: clusterId || undefined,
      selectedBusinessUnitId: businessUnitIdRef.current || undefined,
    })
      .then((result) => {
        if (!isCurrent()) {
          return;
        }
        const facet = result?.[BENCH_REPORT_FACETS.BUSINESS_UNITS] || null;
        setBusinessUnitsData(facet?.roots || []);
        setCountMode(facet?.countMode || FACET_COUNT_MODES.SUBTREE);
        setFailed(false);
        if (shouldClearSelectedBusinessUnit(facet, businessUnitIdRef.current) && onSelectionOutOfScopeRef.current) {
          onSelectionOutOfScopeRef.current();
        }
      })
      .catch((error) => {
        if (!isCurrent()) {
          return;
        }
        // Degrade, don't fail (ADR-108 §5): the page falls back to the plain hierarchy.
        console.warn('Bench business unit options unavailable; falling back to the organization hierarchy', error);
        setFailed(true);
      })
      .finally(() => {
        if (isCurrent()) {
          setIsLoading(false);
        }
      });
  }, [report, clusterId]);

  return { businessUnitsData, countMode, isLoading, failed };
};

export default useBenchBusinessUnitFacets;
